import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, Button, CircularProgress, Alert } from '@mui/material';
import { authService } from '../services/authService';

const Login = () => {
    const [licenseKey, setLicenseKey] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            await authService.login(licenseKey);
            // On success, redirect to dashboard
            navigate('/dashboard');
        } catch (err) {
            setError(err.message || 'Verification failed. Please check your license key.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-gray-900 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-800 via-gray-900 to-black">

            {/* Subtle Animated Background Elements */}
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full mix-blend-screen filter blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-600/20 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-1000"></div>

            <div className="relative z-10 w-full max-w-md p-8 md:p-10 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl">

                <div className="flex flex-col items-center mb-8">
                    <img
                        src="/favicon.png"
                        alt="ARMS Logo"
                        className="w-16 h-16 object-contain mb-4 filter drop-shadow-[0_0_10px_rgba(255,255,255,0.4)]"
                    />
                    <h1 className="text-3xl font-bold tracking-tight text-white mb-1">
                        Welcome to ARMS
                    </h1>
                    <p className="text-gray-300 text-sm">Sign in with your license key</p>
                </div>

                <form onSubmit={handleLogin} className="space-y-6">
                    {error && (
                        <Alert
                            severity="error"
                            className="bg-red-500/10 text-red-200 border border-red-500/30 font-medium rounded-lg"
                        >
                            {error}
                        </Alert>
                    )}

                    <div className="space-y-4">
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="License Key"
                            value={licenseKey}
                            onChange={(e) => setLicenseKey(e.target.value)}
                            required
                            placeholder="XXXX-XXXX-XXXX"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: 'white',
                                    borderRadius: '0.75rem',
                                    bgcolor: 'rgba(255,255,255,0.05)',
                                    backdropFilter: 'blur(10px)',
                                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                                    '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
                                },
                                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
                                '& .MuiInputLabel-root.Mui-focused': { color: '#60a5fa' },
                            }}
                        />
                    </div>

                    <Button
                        type="submit"
                        fullWidth
                        disabled={loading}
                        className="py-3.5 bg-blue-600 hover:bg-blue-500 text-white transition-all duration-300 font-semibold rounded-xl text-lg tracking-wide shadow-[0_0_20px_rgba(37,99,235,0.4)] disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-[0.98]"
                    >
                        {loading ? <CircularProgress size={26} color="inherit" /> : 'Activate'}
                    </Button>
                </form>

                <div className="mt-8 text-center border-t border-white/10 pt-6">
                    <p className="text-gray-400 text-sm">
                        Secured by License Management
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
